/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { formatCurrency, money, toast } from "../../../utils/helpers";

// COMPONENTS
import Select, { createFilter } from "react-select";
import Modal from "../../general/Modal";
import ItemFotos from "../../Items/ItemFotos";
import {
	SelectorImpuesto,
	precioConImpuesto,
} from "../../Consultas/NotasCredito/components/ProductoFacturaNotaCredito";

// En la compra el descuento ya está dentro del total de la línea: el
// precio por unidad (total / cantidad) es neto. Aquí solo se calcula para
// mostrarlo e informarlo, nunca para restarlo otra vez. La línea guarda
// el descuento en monto (tipo "Monto Fijo") o en % (tipo "Porcentaje");
// el monto por unidad se deduce de costo e impuesto, sin depender del tipo.
export const cantidadOriginalCompra = (linea) =>
	parseFloat(linea?.cantidad_original ?? linea?.cantidad) || 0;

// Descuento general de la compra (compra.descuento, en monto): la compra lo
// resta del total, no de las líneas. Se reparte en proporción a cada línea con
// el factor total / (total + descuento), que es como lo aplica la compra.
export const factorDescuentoGeneral = (compra) => {
	const total = parseFloat(compra?.total) || 0;
	const descuento = parseFloat(compra?.descuento) || 0;
	if (descuento <= 0 || total + descuento <= 0) return 1;
	return total / (total + descuento);
};

// Precio por unidad de la línea tal como quedó en la compra, sin el general.
export const precioBrutoCompra = (linea) =>
	linea?.precio || (linea?.total || 0) / (cantidadOriginalCompra(linea) || 1);

// Lo que de verdad se pagó por unidad: con el descuento general repartido.
// `factor_descuento_general` lo pone el formulario al cargar la compra.
export const precioUnidadCompra = (linea) =>
	precioBrutoCompra(linea) * (linea?.factor_descuento_general ?? 1);

// Parte del descuento general que le toca a una unidad de la línea.
export const descuentoGeneralUnidad = (linea) =>
	precioBrutoCompra(linea) - precioUnidadCompra(linea);

export const descuentoLineaCompra = (linea) => {
	const cantidad = cantidadOriginalCompra(linea);
	const costo = parseFloat(linea?.costo) || 0;
	if (!linea || !cantidad || !costo || !parseFloat(linea.descuento)) {
		return { porcentaje: 0, montoUnidad: 0 };
	}

	// La compra guarda el impuesto sobre el costo sin descuento (costo × tasa),
	// pero el total lo aplica sobre el costo ya descontado:
	// total = (costo - descuento) × cantidad × (1 + tasa).
	const tasa = (parseFloat(linea.totalImpuesto) || 0) / (cantidad * costo);
	const netoSinImpuesto = (parseFloat(linea.total) || 0) / cantidad / (1 + tasa);
	const montoUnidad = Math.max(0, Math.round((costo - netoSinImpuesto) * 100) / 100);

	return {
		porcentaje: Math.round((montoUnidad / costo) * 10000) / 100,
		montoUnidad,
	};
};

const ProductoCompraNotaCredito = React.memo(
	({
		productos = [],
		impuestos = [],
		productoSelected = {},
		index,
		checkProductoRepetido,
		removeProductoSelected,
		isEdit,
		handleInputChange,
		isFactura,
		enableProducto,
        estaVencida,
	}) => {
		const [precioManual, setPrecioManual] = useState(0);
		const [showDelete, setShowDelete] = useState(false);
		const [showPINModal, setShowPINModal] = useState(false);
		const [showFotos, setShowFotos] = useState(false);
		const [pinSeguridad, setPinSeguridad] = useState(false);
        const [pierdeImpuestos, setPierdeImpuestos] = useState(isEdit ? productoSelected?.pierdeImpuesto : estaVencida);

		// CALCULOS FINALES
		const [total, setTotal] = useState(0);
        const [importe, setImporte] = useState(0);

		const productLabel = useCallback(
			(producto) => {
				return (
					<span className="row px-0 align-items-center">
						<span
							className="col-2 pr-1"
							style={{
								minWidth: "100px",
								wordBreak: "break-all",
							}}
						>
							{producto?.referencia}
						</span>
						<span className="col-5 px-1">{producto?.nombre}</span>
						<span
							className="col-1 px-1"
							style={{
								fontWeight: "bold",
								color: "#008aab",
							}}
						>
							{isNaN(parseFloat(producto.cantidad))
								? "∞"
								: parseFloat(producto.cantidad)}
						</span>
						<span
							className="col-2 pr-0"
							style={{
								fontWeight: "bold",
								color: "#008aab",
							}}
						>
							{formatCurrency(
								money(producto.precio || producto.costo) || 0,
								"$"
							)}
						</span>
					</span>
				);
			},
			[isFactura]
		);

		const onChangeProductoCompra = async ({value: productoID}) => {
			productoSelected.productoID = productoID;

			if (checkProductoRepetido(productoID, index, productoSelected.id)) {
				removeProductoSelected(productoSelected.id);
				return;
			}

			const currProducto = productos.find(
                (producto) => {
                    return (producto.item_id === productoID) || producto.nombre === productoID
                }
			);

            if (currProducto.cantidad <= 0) {
                toast('Esta factura no tiene este producto disponible para devolución', 'error');
                return;
            }

            const precio = precioUnidadCompra(currProducto);
			const impuestoCalculado =
                currProducto.totalImpuesto
                    ? (precio / (currProducto.impuesto?.porcentaje + 1)) * currProducto.impuesto?.porcentaje
                    : 0;
			// Al elegir el producto la cantidad vuelve a 1.
			const { porcentaje, montoUnidad } = descuentoLineaCompra(currProducto);
			// Se informa junto con el descuento general; ninguno se resta del total.
			const descuentoUnidad = montoUnidad + descuentoGeneralUnidad(currProducto);

            const rimporte = currProducto.importe ||
                precio /
                    (currProducto.impuesto?.porcentaje + 1);

            const rtotal = calcularTotal(precio, rimporte, 1, pierdeImpuestos);

			handleInputChange(
				null,
				{
					id: productoSelected.id,
					productoID: productoSelected.productoID,
					itemID: currProducto?.item_id,
					nombre: (
						<>
                            {currProducto?.referencia && (
                                <span
								className="col-3 pl-1"
								style={{
									minWidth: "100px",
									wordBreak: "break-all !important",
								}}
							>
								{currProducto?.referencia}
							</span>
                            )}
							<span className="col-8">
								{currProducto?.nombre}
							</span>
						</>
					),
					cantidad: 1,
					importe: rimporte,
					descuento: porcentaje,
					descuentoUnidad,
					existencia: currProducto.cantidad,
					unidad: currProducto?.unidad,
					impuesto: {
						value: currProducto.impuesto?.id,
						label: currProducto.impuesto?.nombre,
						porcentaje: currProducto.impuesto?.porcentaje,
						tipo: currProducto?.impuesto?.tipo,
					},
					impuestoCalculado: pierdeImpuestos ? 0 : impuestoCalculado,
					precioVenta: precio,
					total: rtotal,
					descuentoCalculado: descuentoUnidad,
					tipo: currProducto.tipo
				},
				productoSelected.id,
				false
			);
			setPrecioManual(precio);
			setTotal(rtotal);
            setImporte(rimporte);
		};

		const onChangeInput = (field, value) =>
			handleInputChange(field, value, productoSelected.id);

		// Conserva el importe sin impuesto; ver precioConImpuesto.
		const onChangeImpuesto = (opcion) => {
			if (!opcion) return;

			const importeUnidad = parseFloat(productoSelected.importe) || 0;
			const { precio, impuestoUnidad } = precioConImpuesto(importeUnidad, opcion.porcentaje);
			const cantidad = productoSelected.cantidad || 1;
			const totalNuevo = calcularTotal(precio, importeUnidad, cantidad, pierdeImpuestos);

			setPrecioManual(precio);
			setTotal(totalNuevo);

			onChangeInput("impuesto", {
				value: opcion.value,
				label: opcion.label,
				porcentaje: parseFloat(opcion.porcentaje) || 0,
				tipo: opcion.tipo,
			});
			onChangeInput("precioVenta", precio);
			onChangeInput("total", totalNuevo);
			onChangeInput("impuestoCalculado", pierdeImpuestos ? 0 : impuestoUnidad * cantidad);
		};

		// Con 0 o vacío el efecto de cantidad no recalcula y la línea se
		// guardaba con el total de la cantidad anterior.
		const checkQuantityConstrains = () => {
			if (!productoSelected.productoID) return;
			if (parseFloat(productoSelected.cantidad) > 0) return;

			onChangeInput("cantidad", 1);
			toast("La cantidad a devolver debe ser mayor que 0", "error");
		};

		const toggleDeleteModal = () => {
			setShowDelete((state) => !state);
		};

		const toggleFotosModal = () => {};
		const checkPINSeguridad = () => {};
		const togglePINModal = () => {};
		const errors = {};

        // El precio ya es neto del descuento de la compra (ver arriba).
        const calcularTotal = (precio, importe, cantidad, pierdeImpuestos) => {
            return (pierdeImpuestos ? importe : precio ) * cantidad;
        }

		useEffect(() => {
			if (!productoSelected.cantidad || !productoSelected.existencia)
				return;

			if (productoSelected.cantidad <= 0) {
				onChangeInput("cantidad", 1);
				toast("No incluir cantidades menores a 1", "error");
				return;
			} else if (
				productoSelected.cantidad > productoSelected.existencia
			) {
				onChangeInput("cantidad", productoSelected.existencia);
				toast(
					"Cantidad maxima permitida es: " +
						productoSelected.existencia,
					"error"
				);
				return;
			}

			const totalActual = calcularTotal(
				precioManual,
				productoSelected.importe,
				productoSelected.cantidad,
				pierdeImpuestos
			);

			setTotal(totalActual);
			handleInputChange("total", totalActual, productoSelected.id);
			handleInputChange(
				"descuentoCalculado",
				(productoSelected.descuentoUnidad || 0) * productoSelected.cantidad,
				productoSelected.id
			);
			handleInputChange(
				"impuestoCalculado",
				pierdeImpuestos ? 0 : ((productoSelected.precioVenta) / (productoSelected.impuesto?.porcentaje + 1)) * productoSelected.impuesto?.porcentaje * productoSelected.cantidad,
				productoSelected.id
			);
		}, [productoSelected.cantidad, pierdeImpuestos]);

		useEffect(() => {
			if (!isEdit) return;
			setPrecioManual(productoSelected.precioVenta);

			const t = calcularTotal(
                productoSelected.precioVenta,
                productoSelected.importe,
                productoSelected.cantidad,
                pierdeImpuestos
            );

			setTotal(t);
			handleInputChange(
				"total",
				t,
				productoSelected.id
			);
			handleInputChange(
				"impuestoCalculado",
				pierdeImpuestos ? 0 : productoSelected.impuestoCalculado * productoSelected.cantidad,
				productoSelected.id
			);
		}, []);

		return (
			<>
				<tr>
					<td colSpan={2}>
						<input
							type="hidden"
							name={`productos[${index}][id]`}
							value={isEdit ? (productoSelected.id || productoSelected.nombre) : "random1"}
						/>

						<Select
							type="text"
							filterOption={createFilter({
								ignoreAccents: false,
							})}
							// filterOption={filterOption}
							name={{
                                'producto': `productos[${index}][producto_id]`,
                                'item': `productos[${index}][item_id]`,
                                'suministro': `productos[${index}][nombre_producto]`,
                            }[productoSelected.tipo]}
							placeholder={"Seleccione..."}
							value={
								productoSelected.id && {
									value: {
                                        'producto'  : productoSelected.productoID,
                                        'item'      : productoSelected.itemID,
                                        'suministro': productoSelected.productoID,
                                    }[productoSelected.tipo],
									label: productoSelected.nombre,
								}
							}
							onChange={(e) =>
								onChangeProductoCompra(e)
							}
							styles={{
								singleValue: (provided, state) => ({
									...provided,
									width: "100%",
									display: "flex",
								}),
								option: (provided, state) => ({
									...provided,
									backgroundColor: state.isSelected
										? "rgba(152, 204, 245,.3)"
										: "white",
									color: state.isSelected
										? "#008aab"
										: "#666666",
									fontWeight: state.isSelected
										? "600"
										: "normal",
								}),
							}}
							maxMenuHeight={200}
							options={
								productos &&
								productos.map((producto) => ({
									label: `${producto?.referencia} ${producto?.nombre}`,
									value: {
                                        'producto': producto?.item_id,
                                        'item': producto?.item_id,
                                        'suministro': producto.nombre,
                                    }[producto.tipo],
									producto: producto,
								}))
							}
							formatOptionLabel={(option) =>
								option.producto
									? productLabel(option.producto)
									: option.label
							}
							isDisabled={!enableProducto}
						/>
					</td>
					<td>
                        <input
                            type="number"
                            name={`productos[${index}][cantidad]`}
                            placeholder="Cantidad"
                            value={productoSelected.cantidad || undefined}
                            step="1"
                            min="1"
                            style={{maxWidth: "95px"}}
                            onChange={(e) => {
                                onChangeInput(
                                    "cantidad",
                                    parseFloat(e.target.value)
                                );
                            }}
                            onBlur={() => checkQuantityConstrains()}
                            className="form-control"
							readonly={!enableProducto}
                        />
					</td>
					<td>
                        {productoSelected.unidad || 'UND'}
					</td>
					<td>
                        {formatCurrency(
                            money(productoSelected?.importe)
                        )}
						<input
							type="hidden"
							name={`productos[${index}][importe]`}
							className="form-control"
							value={productoSelected.importe}
							readOnly
						/>
					</td>
					<td>
                        <>
                            {pierdeImpuestos ? (
                                <del>{productoSelected.impuesto?.label}</del>
                            ) : enableProducto && productoSelected.productoID ? (
                                <SelectorImpuesto
                                    impuestos={impuestos}
                                    impuesto={productoSelected.impuesto}
                                    onChange={onChangeImpuesto}
                                />
                            ) : (
                                <>{productoSelected.impuesto?.label}</>
                            )}
                            <input
                                type="hidden"
                                name={`productos[${index}][impuesto_id]`}
                                value={productoSelected.impuesto?.value}
                            />
                            <input
                                type="hidden"
                                name={`productos[${index}][impuestos]`}
                                value={
                                    ((productoSelected.precioVenta -
                                        productoSelected.importe) *
                                    productoSelected.cantidad) || 0
                                }
                            />
                        </>
					</td>
					<td>
                        {formatCurrency(
                            money(precioManual),
                            ""
                        )}
                        <input
                            type="hidden"
                            name={`productos[${index}][precio_facturado]`}
                            value={precioManual}
                        />
					</td>
					<td>
                        {productoSelected.descuento ? `${money(productoSelected.descuento)}%` : ""}
                        <input
                            type="hidden"
                            name={`productos[${index}][descuento]`}
                            value={productoSelected.descuento || 0}
                        />
					</td>
                    {estaVencida && (
                        <td>
                            <label className='switch switch-3d switch-success mr-3'>
                                <input type='hidden' name='pierde_impuesto' value={pierdeImpuestos ? 'si' : 'no'} />
                                <input
                                    type='checkbox'
                                    className='switch-input'
                                    id='pierde_impuestos'
                                    name={`productos[${index}][pierde_impuesto]`}
                                    value='si'
                                    checked={pierdeImpuestos}
                                    onChange={() => setPierdeImpuestos(prev => !prev)}
                                />
                                <span className='switch-label' />
                                <span className='switch-handle' />
                            </label>
                        </td>
                    )}
					<td className="text-right">
						<input
							type="hidden"
							name={`productos[${index}][total]`}
							value={total}
						/>
						{formatCurrency(money(total))}
					</td>
					<td className="text-center">
						{productoSelected.fotos?.length > 0 && (
							<button
								type="button"
								title="Ver Fotos"
								onClick={() => toggleFotosModal()}
								className="producto"
							>
								<i className="fas fa-eye"></i>
							</button>
						)}
					</td>
					<td className="text-right">
						<button
							type="button"
							className="btn btn-danger"
							onClick={() =>
								isEdit
									? toggleDeleteModal()
									: removeProductoSelected(
											productoSelected.id
									  )
							}
						>
							<i className="fas fa-trash"></i>
						</button>
					</td>
				</tr>
				{errors[`productos.${index}.total`] && (
					<small
						className="help-blockParams form-Text d-block"
						style={{ whiteSpace: "nowrap" }}
					>
						{errors[`productos.${index}.total`][0]}
					</small>
				)}
				{productoSelected?.fotos && (
					<Modal
						show={showFotos}
						blank={true}
						toggle={() => setShowFotos((state) => !state)}
					>
						<ItemFotos
							toggle={() => setShowFotos((state) => !state)}
							fotos={productoSelected.fotos}
						/>
					</Modal>
				)}
				<Modal
					title="Borrar Producto"
					show={showDelete}
					callback={() => removeProductoSelected(productoSelected.id)}
					acceptText="Borrar"
					toggle={toggleDeleteModal}
				>
					¿Estás seguro de que deseas borrar este producto?
				</Modal>
				{/* <---PIN DE SEGURIDAD MODAL--- */}
				<Modal
					title="PIN de seguridad"
					show={showPINModal}
					callback={() => checkPINSeguridad()}
					acceptText="Aceptar"
					toggle={() => togglePINModal()}
					size="419px"
				>
					<div className="form-group">
						<label>PIN de seguridad</label>
						<input
							type="password"
							className="form-control"
							id="PIN"
							name="pin_seguridad"
							value={pinSeguridad}
							onChange={(e) => setPinSeguridad(e.target.value)}
						/>
						<p
							className="font-weight-bold"
							style={{
								color: "gray",
								fontStyle: "italic",
								lineHeight: "13px",
								fontSize: "0.7rem",
							}}
						>
							Para continuar ingrese PIN de seguridad
						</p>
					</div>
				</Modal>
			</>
		);
	}
);

export default ProductoCompraNotaCredito;
